import { z } from 'zod';

import { GithubPatStatusSchema } from '../github-pat';

// ---------------------------------------------------------------------------
// Provider credentials (generalised from github-pat.ts)
//
// kruxit has no database, so a user's connected provider credentials are split
// across Clerk metadata. Each *secret* field is encrypted (Infisical KMS) and the
// ciphertext lives in server-only `privateMetadata.integrations`; a non-sensitive
// status (account label, last 4 of each secret, capabilities, when added) lives
// in `publicMetadata.integrationStatus` so the client can render connection state
// via `useUser()` without an extra round-trip. Plaintext secrets are never
// persisted, logged, or returned.
// ---------------------------------------------------------------------------

/**
 * The providers a user can connect. A **closed enum**: route params are checked
 * against this before any registry lookup, so unchecked text never indexes a map.
 */
export const ProviderIdSchema = z.enum([
  'github',
  'linear',
  'stripe',
  'datadog',
  'codecov',
]);
export type ProviderId = z.infer<typeof ProviderIdSchema>;

/**
 * Datadog data-residency sites. Allowlisted because the value is interpolated
 * into the API host (`https://api.{site}`) — accepting free text here would be an
 * SSRF vector.
 */
export const DatadogSiteSchema = z.enum([
  'datadoghq.com',
  'us3.datadoghq.com',
  'us5.datadoghq.com',
  'datadoghq.eu',
  'ap1.datadoghq.com',
  'ap2.datadoghq.com',
  'ddog-gov.com',
]);
export type DatadogSite = z.infer<typeof DatadogSiteSchema>;
export const DATADOG_SITES: readonly DatadogSite[] = DatadogSiteSchema.options;
export const DEFAULT_DATADOG_SITE: DatadogSite = 'datadoghq.com';

/**
 * Why validation failed. A safe internal enum that never carries an upstream
 * message; the route maps each to an HTTP status.
 */
export const ValidationReasonSchema = z.enum([
  'invalid_format',
  'invalid_credentials',
  'forbidden',
  'rate_limited',
  'upstream',
  'unknown_provider',
]);
export type ValidationReason = z.infer<typeof ValidationReasonSchema>;

// ── Stored shapes (server-only privateMetadata) ────────────────────────────

/** One encrypted secret field: the ciphertext + the KMS key id it used. */
export const StoredCredentialSchema = z
  .object({
    ciphertext: z.string().min(1),
    keyId: z.string().min(1),
  })
  .readonly();
export type StoredCredential = z.infer<typeof StoredCredentialSchema>;

/**
 * Everything needed to call a provider on the user's behalf: encrypted secret
 * fields keyed by field key, plus any non-secret settings (e.g. a Datadog site)
 * stored in plaintext so a server call can be assembled without the public
 * status. These are immutable DTOs — typed deeply `readonly`.
 */
export const StoredProviderCredentialsSchema = z
  .object({
    credentials: z.record(z.string(), StoredCredentialSchema).readonly(),
    settings: z.record(z.string(), z.string()).readonly().optional(),
  })
  .readonly();
export type StoredProviderCredentials = z.infer<
  typeof StoredProviderCredentialsSchema
>;

// ── Status shape (client-visible publicMetadata) ───────────────────────────

/** Non-secret connection status for one provider. An immutable DTO. */
export const ProviderCredentialStatusSchema = z
  .object({
    accountLabel: z.string(),
    /**
     * Last 4 chars of each secret field, keyed by field key. Multi-secret
     * providers (Datadog) show one per key, not a single ambiguous value.
     */
    fieldLast4: z.record(z.string(), z.string()).readonly(),
    capabilities: z.array(z.string()).readonly(),
    addedAt: z.iso.datetime(),
    mode: z.enum(['test', 'live']).optional(),
    credentialType: z.enum(['restricted', 'secret', 'unknown']).optional(),
    site: DatadogSiteSchema.optional(),
  })
  .readonly();
export type ProviderCredentialStatus = z.infer<
  typeof ProviderCredentialStatusSchema
>;

/**
 * The connection id used for a credential stored in the pre-multi-connection
 * shapes (the legacy single GitHub PAT, and the one-per-provider shape from the
 * first integrations release). Their natural account id can't be recovered from
 * metadata alone, so they read as this id until the next save rewrites them.
 */
export const LEGACY_CONNECTION_ID = '__legacy';

/**
 * Read every connected account for a provider from Clerk `publicMetadata`, keyed
 * by connection id. Accepts all three historical shapes, mapping the two
 * single-credential ones to `LEGACY_CONNECTION_ID`:
 *   1. pre-integrations `githubPatStatus`;
 *   2. one-per-provider `integrationStatus[provider]` (a lone status object);
 *   3. the current `integrationStatus[provider]` map of connectionId → status.
 */
export function readProviderStatuses(
  metadata: unknown,
  provider: ProviderId,
): Record<string, ProviderCredentialStatus> {
  const statusMap = (
    metadata as
      | { readonly integrationStatus?: Record<string, unknown> | null }
      | null
      | undefined
  )?.integrationStatus;
  // eslint-disable-next-line security/detect-object-injection -- provider is a closed-enum key
  const entry = statusMap?.[provider];

  // Single-credential shape: the entry itself parses as one status.
  const single = ProviderCredentialStatusSchema.safeParse(entry);
  if (single.success) return { [LEGACY_CONNECTION_ID]: single.data };

  // Multi-connection map: connectionId → status.
  const map = parseStatusMap(entry);
  if (Object.keys(map).length > 0) return map;

  const legacyGithub =
    provider === 'github' ? readLegacyGithubStatus(metadata) : undefined;
  return legacyGithub ? { [LEGACY_CONNECTION_ID]: legacyGithub } : {};
}

/** Parse a `connectionId → status` map, dropping malformed/null entries. */
function parseStatusMap(
  entry: unknown,
): Record<string, ProviderCredentialStatus> {
  const statuses: Record<string, ProviderCredentialStatus> = {};
  if (entry === null || typeof entry !== 'object') return statuses;
  for (const [connectionId, value] of Object.entries(entry)) {
    const parsed = ProviderCredentialStatusSchema.safeParse(value);
    // eslint-disable-next-line security/detect-object-injection -- connectionId is an own key from Object.entries
    if (parsed.success) statuses[connectionId] = parsed.data;
  }
  return statuses;
}

/** Map the legacy `publicMetadata.githubPatStatus` into the generic status. */
function readLegacyGithubStatus(
  metadata: unknown,
): ProviderCredentialStatus | undefined {
  const legacy = (
    metadata as { readonly githubPatStatus?: unknown } | null | undefined
  )?.githubPatStatus;
  const parsed = GithubPatStatusSchema.safeParse(legacy);
  if (!parsed.success) return undefined;
  return {
    accountLabel: parsed.data.login,
    fieldLast4: { token: parsed.data.last4 },
    capabilities: parsed.data.scopes,
    addedAt: parsed.data.addedAt,
  };
}
